import dayjs from 'dayjs';
import { ReceiveManifestQmMatchResult } from '../../../api/qmCrud';
import {
  ManifestPackageInvLineItemProps,
  ManifestPackageProps,
  ManifestSSCCProps
} from '../../../datastore/tripSheets';
import minResLogo from '../../../assets/images/MinRes Logo (CLR-Black).png';

type ReceiveManifestPrintOptions = {
  manifestId: string;
  manifestStatus?: string | null;
  tripReference?: string | null;
  legDestination?: string | null;
  itemStatusFilterLabel: string;
  search: string;
  ssccs: ManifestSSCCProps[];
  getPackagePurchaseType: (pkg: ManifestPackageProps) => string;
  getPackageItemStatus: (pkg: ManifestPackageProps, sscc: ManifestSSCCProps) => string;
  getOpenQmMatchesForPackage: (
    pkg: ManifestPackageProps,
    sscc?: string | number | null
  ) => ReceiveManifestQmMatchResult[];
  getOpenQmMatchesForLine: (
    asn: string,
    line: ManifestPackageInvLineItemProps,
    sscc?: string | number | null
  ) => ReceiveManifestQmMatchResult[];
  getQmReferenceLabel: (match: ReceiveManifestQmMatchResult) => string;
  isPackageSubmitted: (pkg: ManifestPackageProps) => boolean;
  isLineSubmitted: (asn: string | undefined, line: ManifestPackageInvLineItemProps) => boolean;
};

function escapePrintHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function sanitizePrintTitlePart(value: string | number | null | undefined, fallback = ''): string {
  return String(value ?? fallback)
    .trim()
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function waitForDocumentImages(documentToCheck: Document): Promise<void> {
  const images = Array.from(documentToCheck.images);
  if (images.length === 0) return Promise.resolve();

  return Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }
          image.onload = () => resolve();
          image.onerror = () => resolve();
        })
    )
  ).then(() => undefined);
}

function formatIndicatorLabel(
  matches: ReceiveManifestQmMatchResult[],
  isSubmitted: boolean,
  getQmReferenceLabel: (match: ReceiveManifestQmMatchResult) => string
): string {
  return matches.length > 1
    ? `${matches.length} QMs`
    : matches.length === 1
      ? `QM ${getQmReferenceLabel(matches[0])}`
      : isSubmitted
        ? 'Submitted'
        : '';
}

function getPrintStyles(): string {
  return `
      * { box-sizing: border-box; }
      body {
        margin: 16px;
        color: #111827;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 10px;
      }
      h1 {
        margin: 0;
        font-size: 22px;
      }
      .print-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 12px;
      }
      .print-logo {
        display: block;
        width: 132px;
        height: auto;
        object-fit: contain;
      }
      .summary {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px 16px;
        margin-bottom: 14px;
        padding: 12px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        background: #f9fafb;
      }
      .label {
        color: #6b7280;
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
      }
      .value {
        margin-top: 2px;
        font-size: 12px;
        font-weight: 700;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
      }
      thead {
        display: table-header-group;
      }
      th, td {
        padding: 5px 6px;
        border-bottom: 1px solid #e5e7eb;
        text-align: left;
        vertical-align: top;
      }
      th {
        background: #f3f4f6;
        font-size: 10px;
        text-transform: uppercase;
      }
      td {
        word-break: break-word;
      }
      .identifier,
      .nowrap {
        overflow-wrap: normal;
        word-break: normal;
        white-space: nowrap;
      }
      .description {
        overflow-wrap: anywhere;
      }
      .indicator {
        color: #c2410c;
        font-weight: 700;
        white-space: nowrap;
      }
      .col-indicator { width: 8%; }
      .col-sscc { width: 11%; }
      .col-po { width: 7%; }
      .col-asn { width: 10%; }
      .col-type { width: 8%; }
      .col-package { width: 8%; }
      .col-dimensions { width: 9%; }
      .col-qty { width: 7%; }
      .col-status { width: 9%; }
      .col-description { width: 23%; }
      @media print {
        @page { size: landscape; margin: 8mm 8mm 16mm; }
        body { margin: 0; font-size: 9.5px; }
        .print-document { padding-bottom: 6mm; }
        .summary { break-inside: avoid; }
        tr {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .print-footer {
          position: fixed;
          display: flex;
          justify-content: space-between;
          align-items: center;
          left: 0;
          right: 0;
          bottom: 0;
          color: #6b7280;
          font-size: 9px;
        }
      }
    `;
}

function buildPrintRows(options: ReceiveManifestPrintOptions): string[] {
  return options.ssccs.flatMap((sscc) =>
    sscc.packages.flatMap((pkg, packageIndex) => {
      const dimensions = [pkg.width, pkg.height, pkg.depth]
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .join(' x ');
      const purchaseType = options.getPackagePurchaseType(pkg);
      const packageQuantity = `${pkg.quantity ?? 0} of ${pkg.maxQuantity ?? 0}`;
      const itemStatus = options.getPackageItemStatus(pkg, sscc);
      const inventoryLinesForPrint = pkg.inventoryLines?.length ? pkg.inventoryLines : [null];

      return inventoryLinesForPrint.map((line, lineIndex) => {
        const isFirstLineForPackage = lineIndex === 0;
        const canUseLineIndicator = Boolean(line && String(pkg.asn || '').trim());
        const lineOpenQmMatches =
          canUseLineIndicator && line
            ? options.getOpenQmMatchesForLine(pkg.asn, line, sscc.ssccId)
            : options.getOpenQmMatchesForPackage(pkg, sscc.ssccId);
        const lineIndicatorLabel = formatIndicatorLabel(
          lineOpenQmMatches,
          canUseLineIndicator && line ? options.isLineSubmitted(pkg.asn, line) : options.isPackageSubmitted(pkg),
          options.getQmReferenceLabel
        );
        const lineDescription = line?.description || '-';
        const sourcePartId = String(
          [line?.sourcePartId, line?.partId].find(
            (value) => value !== null && value !== undefined && String(value).trim() !== ''
          ) ?? ''
        ).trim();
        const lineQuantity = line?.quantityReceived ?? null;
        const lineDescriptionWithQuantity =
          lineQuantity !== null && lineQuantity !== undefined && Number(lineQuantity) !== 0
            ? `${lineDescription} (Qty: ${lineQuantity})`
            : lineDescription;
        const lineDescriptionWithPart =
          sourcePartId && lineDescriptionWithQuantity !== '-'
            ? `${sourcePartId} - ${lineDescriptionWithQuantity}`
            : sourcePartId || lineDescriptionWithQuantity;

        return `
          <tr>
            <td class="indicator">${escapePrintHtml(lineIndicatorLabel)}</td>
            <td class="identifier">${packageIndex === 0 && isFirstLineForPackage ? escapePrintHtml(sscc.ssccId) : ''}</td>
            <td class="identifier">${isFirstLineForPackage ? escapePrintHtml(pkg.poNumber || '-') : ''}</td>
            <td class="identifier">${isFirstLineForPackage ? escapePrintHtml(pkg.asn || '-') : ''}</td>
            <td>${isFirstLineForPackage ? escapePrintHtml(purchaseType) : ''}</td>
            <td>${isFirstLineForPackage ? escapePrintHtml(pkg.packageType || '-') : ''}</td>
            <td class="nowrap">${isFirstLineForPackage ? escapePrintHtml(dimensions || '-') : ''}</td>
            <td class="nowrap">${isFirstLineForPackage ? escapePrintHtml(packageQuantity) : ''}</td>
            <td>${isFirstLineForPackage ? escapePrintHtml(itemStatus) : ''}</td>
            <td class="description">${escapePrintHtml(lineDescriptionWithPart)}</td>
          </tr>
        `;
      });
    })
  );
}

function buildReceiveManifestPrintDocument(options: ReceiveManifestPrintOptions): string {
  const printedAt = dayjs().format('DD/MM/YYYY HH:mm');
  const printedFileTimestamp = dayjs().format('YYYYMMDD-HHmm');
  const printLogoUrl = minResLogo.startsWith('data:')
    ? minResLogo
    : new URL(minResLogo, window.location.origin).toString();
  const normalizedSearch = options.search.trim();
  const visiblePackageCount = options.ssccs.reduce((count, sscc) => count + sscc.packages.length, 0);
  const printDocumentTitle = [
    'Receive Manifest',
    sanitizePrintTitlePart(options.manifestId),
    options.tripReference ? `Trip ${sanitizePrintTitlePart(options.tripReference)}` : '',
    sanitizePrintTitlePart(options.legDestination),
    sanitizePrintTitlePart(options.itemStatusFilterLabel),
    normalizedSearch ? `Search ${sanitizePrintTitlePart(normalizedSearch)}` : '',
    printedFileTimestamp
  ]
    .filter(Boolean)
    .join(' - ');
  const rows = buildPrintRows(options);

  const renderPrintTable = () => `
      <table>
        <colgroup>
          <col class="col-indicator" />
          <col class="col-sscc" />
          <col class="col-po" />
          <col class="col-asn" />
          <col class="col-type" />
          <col class="col-package" />
          <col class="col-dimensions" />
          <col class="col-qty" />
          <col class="col-status" />
          <col class="col-description" />
        </colgroup>
        <thead>
          <tr>
            <th>Indicator</th>
            <th>SSCC</th>
            <th>PO</th>
            <th>ASN</th>
            <th>Type</th>
            <th>Package</th>
            <th>Dimensions</th>
            <th>Package Qty</th>
            <th>Item Status</th>
            <th>Line Description</th>
          </tr>
        </thead>
        <tbody>
          ${rows.join('')}
        </tbody>
      </table>
    `;
  const renderPrintFooter = () => `
      <div class="print-footer">
        <span>${escapePrintHtml(printedAt)}</span>
      </div>
    `;
  const renderPrintHeader = (title: string) => `
      <div class="print-header">
        <h1>${escapePrintHtml(title)}</h1>
        <img class="print-logo" src="${escapePrintHtml(printLogoUrl)}" alt="MinRes" />
      </div>
    `;
  const renderPrintSummary = () => `
      <section class="summary">
        <div><div class="label">Site</div><div class="value">${escapePrintHtml(options.legDestination || '-')}</div></div>
        <div><div class="label">Trip</div><div class="value">${escapePrintHtml(options.tripReference || '-')}</div></div>
        <div><div class="label">Manifest</div><div class="value">${escapePrintHtml(options.manifestId || '-')}</div></div>
        <div><div class="label">Status</div><div class="value">${escapePrintHtml(options.manifestStatus || '-')}</div></div>
        <div><div class="label">Item Status Filter</div><div class="value">${escapePrintHtml(options.itemStatusFilterLabel)}</div></div>
        <div><div class="label">Search</div><div class="value">${escapePrintHtml(normalizedSearch || 'None')}</div></div>
        <div><div class="label">Visible SSCCs</div><div class="value">${options.ssccs.length}</div></div>
        <div><div class="label">Visible Packages</div><div class="value">${visiblePackageCount}</div></div>
      </section>
    `;

  return `
      <!doctype html>
      <html>
        <head>
          <title>${escapePrintHtml(printDocumentTitle)}</title>
          <style>
            ${getPrintStyles()}
          </style>
        </head>
        <body>
          <section class="print-document">
            ${renderPrintHeader('Receive Manifest')}
            ${renderPrintSummary()}
            ${renderPrintTable()}
            ${renderPrintFooter()}
          </section>
        </body>
      </html>
    `;
}

export function openReceiveManifestPrintPreview(options: ReceiveManifestPrintOptions): boolean {
  const existingFrame = document.getElementById('receive-manifest-print-frame');
  existingFrame?.remove();

  const printFrame = document.createElement('iframe');
  printFrame.id = 'receive-manifest-print-frame';
  printFrame.title = 'Receive Manifest Print';
  printFrame.style.position = 'fixed';
  printFrame.style.left = '-10000px';
  printFrame.style.top = '0';
  printFrame.style.width = '1280px';
  printFrame.style.height = '1px';
  printFrame.style.border = '0';
  printFrame.style.opacity = '0';
  printFrame.style.pointerEvents = 'none';
  document.body.appendChild(printFrame);

  const printDocument = printFrame.contentWindow?.document;
  if (!printDocument) {
    printFrame.remove();
    return false;
  }

  printDocument.open();
  printDocument.write(buildReceiveManifestPrintDocument(options));
  printDocument.close();

  let fallbackCleanupTimer: number | null = null;
  const cleanupPrintFrame = () => {
    if (fallbackCleanupTimer !== null) {
      window.clearTimeout(fallbackCleanupTimer);
      fallbackCleanupTimer = null;
    }
    printFrame.contentWindow?.removeEventListener('afterprint', cleanupPrintFrame);
    printFrame.remove();
  };
  printFrame.contentWindow?.addEventListener('afterprint', cleanupPrintFrame, { once: true });

  window.setTimeout(() => {
    void waitForDocumentImages(printDocument)
      .then(() => {
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
        fallbackCleanupTimer = window.setTimeout(cleanupPrintFrame, 5 * 60 * 1000);
      })
      .catch(() => {
        cleanupPrintFrame();
      });
  }, 100);

  return true;
}
